import { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  Divider,
  Link,
  FormControl,
  FormLabel,
  FormControlLabel,
  Radio,
  RadioGroup,
  IconButton,
  Popover
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { supabase } from "../supabaseClient";
import { logger } from "../utils/logger";
import { isAtLeast18, computeAgeYears } from "../utils/ageRange";
import * as groupsService from "../services/groupsService";
import * as usersService from "../services/usersService";
import TermsOfService from "./TermsOfService";
import PrivacyPolicy from "./PrivacyPolicy";

import logo from "../assets/wishlist-manager-logo.png";

// One-time identity bootstrap for the founding account: the first time it signs
// in, its Supabase Auth identity is linked to the pre-existing `users` row that
// predates Auth. Configured via env rather than literals so the address isn't
// baked into source control — note that CRA inlines REACT_APP_* values into the
// client bundle at build time, so this is about configurability, not secrecy.
// When either is unset the branch is skipped entirely and the ordinary auth_id
// lookup handles the sign-in, which is what happens for every other account.
const ADMIN_BOOTSTRAP_USER_ID = process.env.REACT_APP_ADMIN_BOOTSTRAP_USER_ID || "";
const ADMIN_BOOTSTRAP_EMAIL = process.env.REACT_APP_ADMIN_BOOTSTRAP_EMAIL || "";

const GoogleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" />
    <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" />
    <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" />
    <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
  </svg>
);

const SNOWFLAKES = Array.from({ length: 35 }, (_, i) => ({
  id: i,
  left: `${((i * 37 + 11) % 97)}%`,
  delay: `${(((i * 13) % 80) / 10).toFixed(1)}s`,
  duration: `${(6 + (i * 7 % 8)).toFixed(1)}s`,
  size: `${2 + (i % 3)}px`,
}));

const STARS = Array.from({ length: 20 }, (_, i) => ({
  id: i,
  left: `${((i * 53 + 7) % 95)}%`,
  top: `${5 + ((i * 31 + 3) % 55)}%`,
  delay: `${(((i * 17) % 40) / 10).toFixed(1)}s`,
  duration: `${(2 + (i % 3)).toFixed(1)}s`,
  size: `${1 + (i % 2)}px`,
}));

const BOKEH = [
  { id: 0, left: '5%',  bottom: '5%', size: 80, color: '#B71C1C', blur: 25, delay: '0s'   },
  { id: 1, left: '14%', bottom: '2%', size: 60, color: '#D4AF37', blur: 22, delay: '1.4s' },
  { id: 2, left: '24%', bottom: '7%', size: 70, color: '#1B5E20', blur: 28, delay: '2.8s' },
  { id: 3, left: '36%', bottom: '1%', size: 50, color: '#B71C1C', blur: 20, delay: '0.7s' },
  { id: 4, left: '50%', bottom: '6%', size: 65, color: '#D4AF37', blur: 24, delay: '2.1s' },
  { id: 5, left: '62%', bottom: '3%', size: 75, color: '#1B5E20', blur: 26, delay: '1.0s' },
  { id: 6, left: '72%', bottom: '8%', size: 58, color: '#B71C1C', blur: 22, delay: '3.3s' },
  { id: 7, left: '82%', bottom: '2%', size: 85, color: '#D4AF37', blur: 30, delay: '0.4s' },
  { id: 8, left: '92%', bottom: '5%', size: 55, color: '#1B5E20', blur: 20, delay: '2.6s' },
];

const SANTA_SLEIGH = (
  <svg viewBox="0 0 330 90" width="330" height="90"
       xmlns="http://www.w3.org/2000/svg" overflow="visible" aria-hidden="true">

    {/* ====== SLEIGH ====== */}
    <path d="M10 22 Q4 33 7 51 L15 57"
          fill="none" stroke="#CC2200" strokeWidth="4" strokeLinecap="round" />
    <path d="M15 57 Q40 65 88 59 Q101 56 103 47 Q103 34 90 29 L68 26
             Q42 24 22 31 Q13 37 13 47 Q13 52 15 57 Z"
          fill="#CC2200" stroke="#1A0800" strokeWidth="1.5" />
    <path d="M22 31 Q42 24 68 26 L90 29 Q101 34 103 42
             Q88 32 65 27 Q40 25 22 31 Z"
          fill="#EE3311" />
    <path d="M22 38 Q45 31 72 34 Q94 36 98 45 Q85 53 68 55 Q44 57 20 51 Z"
          fill="#880000" />
    <path d="M22 31 Q42 24 68 26 L90 29"
          fill="none" stroke="#FFD700" strokeWidth="2.5" />
    <path d="M11 31 Q17 27 15 33 Q12 39 18 38 Q20 38 20 35"
          fill="none" stroke="#FFD700" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M4 65 Q14 74 57 72 Q83 71 104 63"
          fill="none" stroke="#B8860B" strokeWidth="4.5" strokeLinecap="round" />
    <path d="M4 65 Q14 72 57 70 Q83 69 104 62"
          fill="none" stroke="#FFD700" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M4 67 Q1 72 3 77 Q6 81 10 78"
          fill="none" stroke="#B8860B" strokeWidth="3.5" strokeLinecap="round" />
    <line x1="23" y1="58" x2="21" y2="66" stroke="#B8860B" strokeWidth="2.5" />
    <line x1="58" y1="59" x2="57" y2="68" stroke="#B8860B" strokeWidth="2.5" />
    <line x1="92" y1="56" x2="92" y2="64" stroke="#B8860B" strokeWidth="2.5" />

    {/* ====== SANTA ====== */}
    <rect x="69" y="49" width="13" height="13" rx="3" fill="#1A1A1A" stroke="#1A0800" strokeWidth="1" />
    <rect x="57" y="49" width="13" height="13" rx="3" fill="#1A1A1A" stroke="#1A0800" strokeWidth="1" />
    <ellipse cx="64" cy="43" rx="22" ry="15" fill="#CC0000" stroke="#1A0800" strokeWidth="1.5" />
    <ellipse cx="62" cy="38" rx="14" ry="7" fill="#DD2222" />
    <path d="M44 50 Q64 58 84 50" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" />
    <rect x="46" y="41" width="36" height="5" rx="1.5" fill="#1A1A1A" stroke="#1A0800" strokeWidth="0.8" />
    <rect x="60" y="40" width="9" height="7" rx="1" fill="#FFD700" stroke="#1A0800" strokeWidth="0.8" />
    <line x1="64" y1="40" x2="64" y2="47" stroke="#1A0800" strokeWidth="0.8" />
    <line x1="61" y1="43" x2="68" y2="43" stroke="#1A0800" strokeWidth="0.8" />
    <path d="M44 36 Q36 34 30 42" fill="none" stroke="#CC0000" strokeWidth="7" strokeLinecap="round" />
    <path d="M85 36 Q92 38 97 42" fill="none" stroke="#CC0000" strokeWidth="7" strokeLinecap="round" />
    <circle cx="29" cy="42" r="5.5" fill="#CC3300" stroke="#1A0800" strokeWidth="1" />
    <circle cx="98" cy="42" r="5.5" fill="#CC3300" stroke="#1A0800" strokeWidth="1" />
    <path d="M46 31 Q64 24 82 31" fill="white" stroke="#CCCCCC" strokeWidth="0.5" />
    <circle cx="64" cy="20" r="14" fill="#FFCC99" stroke="#1A0800" strokeWidth="1.5" />
    <circle cx="54" cy="22" r="5" fill="#FF8888" opacity="0.45" />
    <circle cx="74" cy="22" r="5" fill="#FF8888" opacity="0.45" />
    <path d="M52 23 Q64 38 76 23 Q76 33 64 39 Q52 33 52 23 Z"
          fill="white" stroke="#CCCCCC" strokeWidth="0.8" />
    <path d="M57 23 Q64 27 71 23" fill="white" stroke="#CCCCCC" strokeWidth="0.8" />
    <circle cx="64" cy="21" r="4" fill="#FF9977" />
    <circle cx="57" cy="16" r="2.5" fill="white" stroke="#1A0800" strokeWidth="0.8" />
    <circle cx="71" cy="16" r="2.5" fill="white" stroke="#1A0800" strokeWidth="0.8" />
    <circle cx="57" cy="16" r="1.4" fill="#1A0800" />
    <circle cx="71" cy="16" r="1.4" fill="#1A0800" />
    <circle cx="57.7" cy="15.3" r="0.7" fill="white" />
    <circle cx="71.7" cy="15.3" r="0.7" fill="white" />
    <path d="M54 13 Q57 11 60 13" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M68 13 Q71 11 74 13" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
    <polygon points="53,14 64,-8 78,14" fill="#CC0000" stroke="#1A0800" strokeWidth="1.5" />
    <path d="M55 13 L64 -8 L67 -3 L59 13 Z" fill="#DD2222" />
    <rect x="49" y="12" width="30" height="7" rx="3.5" fill="white" stroke="#CCCCCC" strokeWidth="1" />
    <circle cx="64" cy="-7" r="5.5" fill="white" stroke="#CCCCCC" strokeWidth="0.8" />

    {/* ====== HARNESS (gold reins) ====== */}
    <line x1="29" y1="41" x2="147" y2="54" stroke="#FFD700" strokeWidth="1.8" strokeOpacity="0.85" />
    <line x1="29" y1="41" x2="207" y2="52" stroke="#FFD700" strokeWidth="1.5" strokeOpacity="0.65" />
    <line x1="29" y1="41" x2="267" y2="50" stroke="#FFD700" strokeWidth="1.2" strokeOpacity="0.5" />

    {/* ====== REINDEER 1 ====== */}
    <ellipse cx="155" cy="61" rx="18" ry="10" transform="rotate(-13 155 61)" fill="#8B4513" stroke="#4A2006" strokeWidth="1.2" />
    <ellipse cx="154" cy="62" rx="12" ry="6" transform="rotate(-13 154 62)" fill="#D2B48C" />
    <ellipse cx="154" cy="57" rx="10" ry="5" transform="rotate(-13 154 57)" fill="#C6862E" />
    <path d="M165 54 Q170 47 174 44" fill="none" stroke="#8B4513" strokeWidth="9" strokeLinecap="round" />
    <path d="M166 53 Q171 47 175 44" fill="none" stroke="#C6862E" strokeWidth="5" strokeLinecap="round" />
    <circle cx="175" cy="42" r="9.5" fill="#A0522D" stroke="#4A2006" strokeWidth="1.2" />
    <ellipse cx="178" cy="45" rx="5.5" ry="3.5" fill="#C6862E" />
    <ellipse cx="171" cy="36" rx="3.5" ry="5" transform="rotate(-20 171 36)" fill="#A0522D" stroke="#4A2006" strokeWidth="1" />
    <ellipse cx="171" cy="36" rx="2" ry="3" transform="rotate(-20 171 36)" fill="#E8A070" />
    <circle cx="177" cy="40" r="2.8" fill="white" stroke="#4A2006" strokeWidth="0.8" />
    <circle cx="177" cy="40" r="1.6" fill="#1A0800" />
    <circle cx="177.8" cy="39.2" r="0.7" fill="white" />
    <ellipse cx="182" cy="46" rx="1.5" ry="1" fill="#4A2006" />
    <path d="M172 34 L167 23 L162 17 M167 23 L170 17 M170 17 L168 13"
          fill="none" stroke="#4A2006" strokeWidth="2.2" strokeLinecap="round" />
    <path d="M175 32 L179 22 L183 16 M179 22 L176 16"
          fill="none" stroke="#4A2006" strokeWidth="2.2" strokeLinecap="round" />
    <line x1="165" y1="68" x2="169" y2="83" stroke="#5D2E0C" strokeWidth="3" strokeLinecap="round" />
    <line x1="159" y1="69" x2="164" y2="84" stroke="#5D2E0C" strokeWidth="3" strokeLinecap="round" />
    <line x1="143" y1="67" x2="138" y2="82" stroke="#5D2E0C" strokeWidth="3" strokeLinecap="round" />
    <line x1="149" y1="66" x2="145" y2="81" stroke="#5D2E0C" strokeWidth="3" strokeLinecap="round" />
    <ellipse cx="169" cy="83" rx="3.5" ry="2" fill="#2A1000" />
    <ellipse cx="164" cy="84" rx="3.5" ry="2" fill="#2A1000" />
    <ellipse cx="138" cy="82" rx="3.5" ry="2" fill="#2A1000" />
    <ellipse cx="145" cy="81" rx="3.5" ry="2" fill="#2A1000" />

    {/* ====== REINDEER 2 ====== */}
    <ellipse cx="215" cy="61" rx="18" ry="10" transform="rotate(-13 215 61)" fill="#8B4513" stroke="#4A2006" strokeWidth="1.2" />
    <ellipse cx="214" cy="62" rx="12" ry="6" transform="rotate(-13 214 62)" fill="#D2B48C" />
    <ellipse cx="214" cy="57" rx="10" ry="5" transform="rotate(-13 214 57)" fill="#C6862E" />
    <path d="M225 54 Q230 47 234 44" fill="none" stroke="#8B4513" strokeWidth="9" strokeLinecap="round" />
    <path d="M226 53 Q231 47 235 44" fill="none" stroke="#C6862E" strokeWidth="5" strokeLinecap="round" />
    <circle cx="235" cy="42" r="9.5" fill="#A0522D" stroke="#4A2006" strokeWidth="1.2" />
    <ellipse cx="238" cy="45" rx="5.5" ry="3.5" fill="#C6862E" />
    <ellipse cx="231" cy="36" rx="3.5" ry="5" transform="rotate(-20 231 36)" fill="#A0522D" stroke="#4A2006" strokeWidth="1" />
    <ellipse cx="231" cy="36" rx="2" ry="3" transform="rotate(-20 231 36)" fill="#E8A070" />
    <circle cx="237" cy="40" r="2.8" fill="white" stroke="#4A2006" strokeWidth="0.8" />
    <circle cx="237" cy="40" r="1.6" fill="#1A0800" />
    <circle cx="237.8" cy="39.2" r="0.7" fill="white" />
    <ellipse cx="242" cy="46" rx="1.5" ry="1" fill="#4A2006" />
    <path d="M232 34 L227 23 L222 17 M227 23 L230 17 M230 17 L228 13"
          fill="none" stroke="#4A2006" strokeWidth="2.2" strokeLinecap="round" />
    <path d="M235 32 L239 22 L243 16 M239 22 L236 16"
          fill="none" stroke="#4A2006" strokeWidth="2.2" strokeLinecap="round" />
    <line x1="225" y1="68" x2="229" y2="83" stroke="#5D2E0C" strokeWidth="3" strokeLinecap="round" />
    <line x1="219" y1="69" x2="224" y2="84" stroke="#5D2E0C" strokeWidth="3" strokeLinecap="round" />
    <line x1="203" y1="67" x2="198" y2="82" stroke="#5D2E0C" strokeWidth="3" strokeLinecap="round" />
    <line x1="209" y1="66" x2="205" y2="81" stroke="#5D2E0C" strokeWidth="3" strokeLinecap="round" />
    <ellipse cx="229" cy="83" rx="3.5" ry="2" fill="#2A1000" />
    <ellipse cx="224" cy="84" rx="3.5" ry="2" fill="#2A1000" />
    <ellipse cx="198" cy="82" rx="3.5" ry="2" fill="#2A1000" />
    <ellipse cx="205" cy="81" rx="3.5" ry="2" fill="#2A1000" />

    {/* ====== RUDOLPH ====== */}
    <ellipse cx="275" cy="61" rx="18" ry="10" transform="rotate(-13 275 61)" fill="#8B4513" stroke="#4A2006" strokeWidth="1.2" />
    <ellipse cx="274" cy="62" rx="12" ry="6" transform="rotate(-13 274 62)" fill="#D2B48C" />
    <ellipse cx="274" cy="57" rx="10" ry="5" transform="rotate(-13 274 57)" fill="#C6862E" />
    <path d="M285 54 Q290 47 294 44" fill="none" stroke="#8B4513" strokeWidth="9" strokeLinecap="round" />
    <path d="M286 53 Q291 47 295 44" fill="none" stroke="#C6862E" strokeWidth="5" strokeLinecap="round" />
    <circle cx="295" cy="42" r="9.5" fill="#A0522D" stroke="#4A2006" strokeWidth="1.2" />
    <ellipse cx="298" cy="45" rx="5.5" ry="3.5" fill="#C6862E" />
    <ellipse cx="291" cy="36" rx="3.5" ry="5" transform="rotate(-20 291 36)" fill="#A0522D" stroke="#4A2006" strokeWidth="1" />
    <ellipse cx="291" cy="36" rx="2" ry="3" transform="rotate(-20 291 36)" fill="#E8A070" />
    <circle cx="297" cy="40" r="2.8" fill="white" stroke="#4A2006" strokeWidth="0.8" />
    <circle cx="297" cy="40" r="1.6" fill="#1A0800" />
    <circle cx="297.8" cy="39.2" r="0.7" fill="white" />
    <path d="M292 34 L287 23 L282 17 M287 23 L290 17 M290 17 L288 13"
          fill="none" stroke="#4A2006" strokeWidth="2.2" strokeLinecap="round" />
    <path d="M295 32 L299 22 L303 16 M299 22 L296 16"
          fill="none" stroke="#4A2006" strokeWidth="2.2" strokeLinecap="round" />
    <line x1="285" y1="68" x2="289" y2="83" stroke="#5D2E0C" strokeWidth="3" strokeLinecap="round" />
    <line x1="279" y1="69" x2="284" y2="84" stroke="#5D2E0C" strokeWidth="3" strokeLinecap="round" />
    <line x1="263" y1="67" x2="258" y2="82" stroke="#5D2E0C" strokeWidth="3" strokeLinecap="round" />
    <line x1="269" y1="66" x2="265" y2="81" stroke="#5D2E0C" strokeWidth="3" strokeLinecap="round" />
    <ellipse cx="289" cy="83" rx="3.5" ry="2" fill="#2A1000" />
    <ellipse cx="284" cy="84" rx="3.5" ry="2" fill="#2A1000" />
    <ellipse cx="258" cy="82" rx="3.5" ry="2" fill="#2A1000" />
    <ellipse cx="265" cy="81" rx="3.5" ry="2" fill="#2A1000" />
    <circle cx="305" cy="42" r="11" fill="#FF2200" className="rudolph-glow" />
    <circle cx="305" cy="42" r="7" fill="#FF4400" />
    <circle cx="305" cy="42" r="5" fill="#FF6600" />
    <circle cx="303" cy="40" r="1.8" fill="rgba(255,255,255,0.75)" />

  </svg>
);

const PresentSVG = ({ color, ribbon, size }) => (
  <svg viewBox="0 0 22 25" width={size} height={size}
       xmlns="http://www.w3.org/2000/svg" overflow="visible" aria-hidden="true">
    <rect x="2" y="10" width="18" height="14" rx="2" fill={color} />
    <rect x="6" y="10" width="3"  height="14" fill="white" opacity="0.12" />
    <rect x="13" y="10" width="3" height="14" fill="white" opacity="0.12" />
    <rect x="1" y="7" width="20" height="5" rx="2" fill={color} />
    <line x1="2" y1="7.4" x2="20" y2="7.4" stroke="white" strokeWidth="0.9" opacity="0.35" />
    <rect x="9.5" y="7"  width="3" height="17" fill={ribbon} />
    <rect x="2"   y="13" width="18" height="3" fill={ribbon} />
    <ellipse cx="8.5"  cy="6" rx="4" ry="2.5" transform="rotate(-25 8.5 6)"  fill={ribbon} />
    <ellipse cx="13.5" cy="6" rx="4" ry="2.5" transform="rotate(25 13.5 6)"  fill={ribbon} />
    <circle cx="11"   cy="7" r="2.2" fill={color} stroke={ribbon} strokeWidth="0.8" />
    <circle cx="10.2" cy="6.3" r="0.8" fill="white" opacity="0.5" />
  </svg>
);

const TREES = [
  { id: 0,  left: '-1%', height: 140, width: 55 },
  { id: 1,  left: '3%',  height: 95,  width: 38 },
  { id: 2,  left: '7%',  height: 170, width: 66 },
  { id: 3,  left: '12%', height: 120, width: 48 },
  { id: 4,  left: '17%', height: 85,  width: 34 },
  { id: 5,  left: '21%', height: 155, width: 60 },
  { id: 6,  left: '27%', height: 100, width: 40 },
  { id: 7,  left: '33%', height: 130, width: 52 },
  { id: 8,  left: '59%', height: 125, width: 50 },
  { id: 9,  left: '65%', height: 148, width: 57 },
  { id: 10, left: '71%', height: 92,  width: 37 },
  { id: 11, left: '76%', height: 162, width: 63 },
  { id: 12, left: '82%', height: 108, width: 43 },
  { id: 13, left: '87%', height: 138, width: 54 },
  { id: 14, left: '92%', height: 115, width: 46 },
  { id: 15, left: '97%', height: 78,  width: 31 },
];

const PRESENTS = [
  { id: 0, cls: 'present-fall-0', left: 'calc(8vw  - 60px)', top: '38%', size: 54, color: '#CC2200', ribbon: '#FFD700' },
  { id: 1, cls: 'present-fall-1', left: 'calc(19vw - 60px)', top: '25%', size: 42, color: '#1B5E20', ribbon: '#FF6B6B' },
  { id: 2, cls: 'present-fall-2', left: 'calc(33vw - 60px)', top: '10%', size: 66, color: '#1A237E', ribbon: '#FFD700' },
  { id: 3, cls: 'present-fall-3', left: 'calc(52vw - 60px)', top: '8%',  size: 48, color: '#6A1B9A', ribbon: '#E8C96B' },
  { id: 4, cls: 'present-fall-4', left: 'calc(69vw - 60px)', top: '17%', size: 60, color: '#00695C', ribbon: '#FF5252' },
  { id: 5, cls: 'present-fall-5', left: 'calc(83vw - 60px)', top: '31%', size: 45, color: '#E65100', ribbon: '#1B5E20' },
];

const isDuplicateKeyError = (error) =>
  !!error && (error.code === "23505" ||
    /duplicate key value violates unique constraint/i.test(error?.message || ""));

// Latest date of birth that still makes someone 18 today, in UTC (matching the
// ageRange.js / SQL compute_age_range convention). Computed once at module load —
// the sub-day drift over a session is irrelevant, the real gate is isAtLeast18()
// plus the server-side check.
const MAX_SIGNUP_BIRTHDATE = (() => {
  const now = new Date();
  const y = now.getUTCFullYear() - 18;
  const m = String(now.getUTCMonth() + 1).padStart(2, "0");
  const d = String(now.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
})();

// Shared date-of-birth input for both signup routes (email/password and the
// invite / OAuth "create your profile" step). Shows a live 18+ check plus a
// fixed privacy explanation.
function BirthdateField({ value, onChange }) {
  const showAgeCaption = /^\d{4}-\d{2}-\d{2}$/.test(value);
  return (
    <Box>
      <TextField
        label="Date of birth"
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        InputLabelProps={{ shrink: true }}
        inputProps={{ max: MAX_SIGNUP_BIRTHDATE }}
        fullWidth
        sx={{ backgroundColor: "rgba(255,255,255,0.08)", borderRadius: 1 }}
      />
      {showAgeCaption &&
        (isAtLeast18(value) ? (
          <Typography variant="caption" sx={{ color: "success.light", display: "block", mt: 0.5 }}>
            ✓ You're 18 or older
          </Typography>
        ) : (
          <Typography variant="caption" sx={{ color: "error.light", display: "block", mt: 0.5 }}>
            You must be 18 or older to sign up
          </Typography>
        ))}
      <Typography variant="caption" sx={{ color: "rgba(144,202,249,0.85)", display: "block", mt: 0.5 }}>
        We ask your date of birth only to confirm you're 18 or older, as our Terms of Service require. It's stored privately on your profile — other members only ever see an age range (e.g. "35–44"), never the date.
      </Typography>
    </Box>
  );
}

export default function UserPrompt({ onUserSet, sessionAwaitingProfile, passwordRecovery, onExitToLogin }) {
  // Auth states: signIn | signUp | chooseUsername | forgotPassword | resetPassword
  const [mode, setMode] = useState("signIn");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [desiredUserId, setDesiredUserId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [birthdate, setBirthdate] = useState("");
  const [error, setError] = useState("");
  const [infoMsg, setInfoMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [usernameInfoAnchor, setUsernameInfoAnchor] = useState(null);
  const [displayNameInfoAnchor, setDisplayNameInfoAnchor] = useState(null);
  const [legalPage, setLegalPage] = useState(null);

  // Set only when arriving via an admin-approved Group Leader invite email
  const inviteGroupId = sessionAwaitingProfile?.invitedGroupId ?? null;
  const [inviteGroupName, setInviteGroupName] = useState(null);
  const [inviteIsParent, setInviteIsParent] = useState("no");

  useEffect(() => {
    if (!sessionAwaitingProfile) return;
    setMode("chooseUsername");
  }, [sessionAwaitingProfile]);

  useEffect(() => {
    if (!passwordRecovery) return;
    setMode("resetPassword");
  }, [passwordRecovery]);

  useEffect(() => {
    if (!inviteGroupId) return;
    groupsService.getGroup({ groupId: inviteGroupId })
      .then((group) => setInviteGroupName(group?.name ?? null))
      .catch(() => setInviteGroupName(null));
  }, [inviteGroupId]);

  useEffect(() => {
    const prevBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = "#050E14";
    return () => {
      document.body.style.backgroundColor = prevBg || "";
    };
  }, []);

  const resetForm = () => {
    setError("");
    setInfoMsg("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setDesiredUserId("");
    setFirstName("");
    setLastName("");
    setBirthdate("");
    setLoading(false);
    setMode("signIn");
  };

  const handleSignIn = async () => {
    setError("");
    if (!email || !password) return setError("Please enter email and password.");
    setLoading(true);

    try {
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password
      });

      if (authError) {
        setError(authError.message || "Failed to sign in.");
        setLoading(false);
        return;
      }

      const authId = authData.user.id;

      // Founding-account path: set auth_id on that row if not yet linked, then log in
      if (
        ADMIN_BOOTSTRAP_EMAIL &&
        ADMIN_BOOTSTRAP_USER_ID &&
        email.trim().toLowerCase() === ADMIN_BOOTSTRAP_EMAIL.toLowerCase()
      ) {
        await usersService.linkAdminBootstrap({ userId: ADMIN_BOOTSTRAP_USER_ID, authId });
        if (typeof onUserSet === "function") onUserSet(ADMIN_BOOTSTRAP_USER_ID);
        logger.info('UserPrompt', 'signin', { userId: ADMIN_BOOTSTRAP_USER_ID });
        setLoading(false);
        return;
      }

      // Regular user: look up existing profile by auth_id
      const userId = await usersService.getUserIdByAuthId(authId);

      if (userId) {
        if (typeof onUserSet === "function") onUserSet(userId);
        logger.info('UserPrompt', 'signin', { userId });
        setLoading(false);
        return;
      }

      // No profile linked yet — show profile creation screen
      setMode("chooseUsername");
      setLoading(false);
    } catch (e) {
      setError(e?.message || "Sign in failed.");
      setLoading(false);
    }
  };

  const handleOAuthSignIn = async (provider) => {
    setError("");
    setLoading(true);

    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: window.location.origin }
    });

    if (oauthError) {
      setError(oauthError.message || "Failed to start sign-in.");
      logger.error('UserPrompt', 'oauth_signin', { provider, error: oauthError.message });
      setLoading(false);
    }
    // On success the browser navigates to the provider — no further action here.
  };

  const handleForgotPassword = async () => {
    setError("");
    setInfoMsg("");
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return setError("Please enter your email address.");

    setLoading(true);
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(trimmedEmail, {
      redirectTo: window.location.origin,
    });
    setLoading(false);

    if (resetError) {
      setError(resetError.message || "Failed to send reset link.");
      return;
    }

    logger.info('UserPrompt', 'password_reset_requested', { email: trimmedEmail });
    setEmail("");
    setInfoMsg("If that email is registered, a password reset link is on its way. Check your inbox.");
  };

  const handleResetPassword = async () => {
    setError("");

    if (!password || !confirmPassword) return setError("Please fill in both password fields.");
    if (password !== confirmPassword) return setError("Passwords do not match.");

    setLoading(true);

    const { error: updateError } = await supabase.auth.updateUser({ password });
    if (updateError) {
      setLoading(false);
      setError(updateError.message || "Failed to set new password.");
      return;
    }

    const { data: { session } } = await supabase.auth.getSession();
    const authId = session?.user?.id;
    const foundUserId = await usersService.getUserIdByAuthId(authId);

    setLoading(false);

    if (!foundUserId) {
      setError("Password updated, but we couldn't find your profile. Please try signing in.");
      return;
    }

    logger.info('UserPrompt', 'password_reset_completed', { userId: foundUserId });
    setPassword("");
    setConfirmPassword("");
    if (typeof onUserSet === "function") onUserSet(foundUserId);
  };

  const handleSignUp = async () => {
    setError("");

    const desired = desiredUserId.trim();
    const fn = firstName.trim();
    const ln = lastName.trim();

    if (!email || !password || !desired || !fn || !ln)
      return setError("Please fill all fields.");

    if (!birthdate) return setError("Please enter your date of birth.");
    if (computeAgeYears(birthdate) == null) return setError("Please enter a valid date of birth.");
    if (!isAtLeast18(birthdate)) {
      logger.warn("UserPrompt", "signup_denied_underage", { context: "signUp" });
      setError("You must be at least 18 years old to create a Wishlist Manager account. Your account was not created.");
      setMode("signIn");
      return;
    }

    setLoading(true);

    // Ensure desired username is available
    let available;
    try {
      available = await usersService.checkUsernameAvailable(desired);
    } catch {
      setLoading(false);
      setError("Failed to check username availability.");
      return;
    }

    if (!available) {
      setLoading(false);
      setError("Username already taken. Please choose another.");
      return;
    }

    // Create auth user — a DB trigger auto-creates the public.users profile from metadata
    const displayName = `${fn} ${ln}`;
    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: { data: { user_id: desired, display_name: displayName, birthdate } },
    });

    setLoading(false);

    if (signUpError) {
      if (isDuplicateKeyError(signUpError)) {
        logger.warn('UserPrompt', 'signup_duplicate_username', { userId: desired });
        setError("Username already taken. Please choose another.");
      } else {
        setError(signUpError.message || "Sign up failed.");
      }
      return;
    }

    if (!signUpData.session && signUpData.user && signUpData.user.identities?.length === 0) {
      logger.warn('UserPrompt', 'signup_duplicate_email', { email: email.trim() });
      setError("This email is already registered. Please sign in instead.");
      return;
    }

    if (signUpData.session) {
      logger.info('UserPrompt', 'signup', { userId: desired });
      if (typeof onUserSet === "function") onUserSet(desired);
    } else if (signUpData.user) {
      // Auth account created but no session — attempt immediate sign-in.
      // Succeeds when email confirmation is off; fails with "Email not confirmed" when on.
      const { data: signInData } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (signInData?.session) {
        const authId = signInData.session.user.id;
        const foundUserId = await usersService.getUserIdByAuthId(authId);
        if (foundUserId) {
          logger.info('UserPrompt', 'signup', { userId: foundUserId });
          if (typeof onUserSet === "function") onUserSet(foundUserId);
        } else {
          const displayName = `${fn} ${ln}`;
          try {
            await usersService.createUserProfile({ userId: desired, displayName, authId });
            logger.info('UserPrompt', 'signup', { userId: desired });
            if (typeof onUserSet === "function") onUserSet(desired);
          } catch (insertErr) {
            if (isDuplicateKeyError(insertErr)) {
              logger.warn('UserPrompt', 'signup_duplicate_username', { userId: desired });
              setError("Username already taken. Please choose another, then sign in.");
            } else {
              setError("Account created but profile setup failed. Please try signing in.");
            }
          }
        }
      } else {
        resetForm();
        setInfoMsg("Account created! Please check your email to confirm your account, then sign in.");
      }
    } else {
      resetForm();
      setInfoMsg("Account created! Please check your email to confirm your account, then sign in.");
    }
  };

  // When arriving via an invite link, add the new profile to the pre-approved
  // group directly — this is what lets the recipient skip GroupSelection.js
  // entirely (it only shows when a user has no primary_group_id).
  const joinInvitedGroup = async (desired) => {
    if (!inviteGroupId) return true;
    try {
      await groupsService.acceptGroupInvite();
      await usersService.acceptInvite({ userId: desired, isParent: inviteIsParent === "yes" });
      logger.info('UserPrompt', 'invite_accepted', { userId: desired, groupId: inviteGroupId });
      return true;
    } catch (e) {
      logger.error('UserPrompt', 'invite_accepted', { error: e.message, userId: desired, groupId: inviteGroupId });
      return false;
    }
  };

  const handleChooseUsername = async () => {
    setError("");

    const desired = desiredUserId.trim();
    const fn = firstName.trim();
    const ln = lastName.trim();

    if (!desired || !fn || !ln) return setError("Please fill all fields.");
    if (inviteGroupId && !password) return setError("Please choose a password.");

    if (!birthdate) return setError("Please enter your date of birth.");
    if (computeAgeYears(birthdate) == null) return setError("Please enter a valid date of birth.");
    if (!isAtLeast18(birthdate)) {
      logger.warn("UserPrompt", "signup_denied_underage", { context: "chooseUsername" });
      try { await supabase.auth.signOut(); } catch (e) { /* already effectively signed out */ }
      setError("You must be at least 18 years old to use Wishlist Manager. Your profile was not created and you've been signed out.");
      setMode("signIn");
      setLoading(false);
      if (typeof onExitToLogin === "function") onExitToLogin();
      return;
    }

    setLoading(true);

    // Get current session auth UUID
    const { data: { session } } = await supabase.auth.getSession();
    const authId = session?.user?.id;

    if (!authId) {
      setLoading(false);
      return setError("Session expired. Please sign in again.");
    }

    // Invited accounts have no password set yet — set one before anything else.
    // Either way, persist the signup date of birth into auth metadata so App.js's
    // post-login profile load can write it through to name_profiles.birthdate.
    if (inviteGroupId) {
      const { error: pwError } = await supabase.auth.updateUser({ password, data: { birthdate } });
      if (pwError) {
        setLoading(false);
        setError(pwError.message || "Failed to set password.");
        return;
      }
    } else {
      const { error: bdError } = await supabase.auth.updateUser({ data: { birthdate } });
      if (bdError) {
        setLoading(false);
        return setError(bdError.message || "Failed to save your details. Please try again.");
      }
    }

    let existing;
    try {
      existing = await usersService.getProfileByUserId(desired);
    } catch {
      setLoading(false);
      setError("Failed to check username availability.");
      return;
    }

    if (existing?.user_id) {
      if (existing.auth_id) {
        // Username is already claimed by another authenticated user
        setLoading(false);
        setError("Username already taken. Please choose another.");
        return;
      }
      // Existing profile without auth_id — link it to this session
      try {
        await usersService.linkAuthId({ userId: desired, authId });
      } catch {
        setLoading(false);
        return setError("Failed to link your profile.");
      }
      const joined = await joinInvitedGroup(desired);
      setLoading(false);
      if (!joined) {
        setError("Failed to join the group. Please try again.");
        return;
      }
      logger.info('UserPrompt', 'signin', { userId: desired });
      if (typeof onUserSet === "function") onUserSet(desired);
      return;
    }

    // Brand new profile
    const displayName = `${fn} ${ln}`;
    try {
      await usersService.createUserProfile({ userId: desired, displayName, authId });
    } catch (insertErr) {
      setLoading(false);
      if (isDuplicateKeyError(insertErr)) {
        logger.warn('UserPrompt', 'signup_duplicate_username', { userId: desired });
        setError("Username already taken. Please choose another.");
      } else {
        setError("Failed to create profile.");
      }
      return;
    }

    const joined = await joinInvitedGroup(desired);
    setLoading(false);

    if (!joined) {
      setError("Failed to join the group. Please try again.");
      return;
    }

    logger.info('UserPrompt', 'signup', { userId: desired });
    if (typeof onUserSet === "function") onUserSet(desired);
  };

  if (legalPage === "terms") return <TermsOfService homeLabel="Back" onNavigateHome={() => setLegalPage(null)} onNavigate={setLegalPage} />;
  if (legalPage === "privacy") return <PrivacyPolicy homeLabel="Back" onNavigateHome={() => setLegalPage(null)} onNavigate={setLegalPage} />;

  return (
    <>
      <div className="login-canvas">
        {SNOWFLAKES.map(sf => (
          <span
            key={sf.id}
            className="login-snowflake"
            style={{
              left: sf.left,
              width: sf.size,
              height: sf.size,
              animationDelay: sf.delay,
              animationDuration: sf.duration,
            }}
          />
        ))}
        {STARS.map(st => (
          <span
            key={st.id}
            className="login-star"
            style={{
              left: st.left,
              top: st.top,
              width: st.size,
              height: st.size,
              animationDelay: st.delay,
              animationDuration: `${st.duration}s`,
            }}
          />
        ))}
        {BOKEH.map(b => (
          <div
            key={b.id}
            className="login-bokeh"
            style={{
              left: b.left,
              bottom: b.bottom,
              width: b.size,
              height: b.size,
              background: `radial-gradient(circle, ${b.color} 0%, transparent 70%)`,
              filter: `blur(${b.blur}px)`,
              animationDelay: b.delay,
              animationDuration: '8s',
            }}
          />
        ))}
        {TREES.map(t => (
          <div
            key={t.id}
            className="login-tree"
            style={{
              left: t.left,
              width: t.width,
              height: t.height,
            }}
          />
        ))}
        {PRESENTS.map(p => (
          <div
            key={p.id}
            className={`login-present ${p.cls}`}
            style={{ left: p.left, top: p.top }}
          >
            <PresentSVG color={p.color} ribbon={p.ribbon} size={p.size} />
          </div>
        ))}
        <div className="login-santa">
          {SANTA_SLEIGH}
        </div>
      </div>
      <div className="login-content">
        <Box sx={{ display: "flex", flexDirection: "column", width: "100%", maxWidth: 520 }}>
        <Box
          component="img"
          src={logo}
          alt="Wishlist Manager"
          sx={{
            width: "100%",
            height: "auto",
            display: "block",
            mx: "auto",
            mb: 0,
          }}
        />
      <Paper
        elevation={6}
        className="auth-panel"
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          p: 4,
          textAlign: "center",
          backgroundColor: "transparent",
          boxShadow: "none",
          border: "none",
          color: "#90CAF9",
          "& .MuiInputBase-input": {
            color: "#f8fafc"
          },
          "& .MuiInputLabel-root": {
            color: "rgba(255,255,255,0.75)"
          },
          "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(255,255,255,0.16)"
          },
          "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(255,255,255,0.32)"
          }
        }}
      >

        {mode === "chooseUsername" ? (
          <>
            <Typography variant="h5" sx={{ mb: 1, color: "#90CAF9" }}>
              Create your profile
            </Typography>

            {inviteGroupId && (
              <Typography variant="body2" sx={{ mb: 2, color: "rgba(144,202,249,0.85)" }}>
                You've been invited to join <strong>{inviteGroupName || "your group"}</strong>.
              </Typography>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Stack spacing={2}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TextField
                  label="Username"
                  value={desiredUserId}
                  onChange={(e) => setDesiredUserId(e.target.value)}
                  autoFocus
                  sx={{
                    flexGrow: 1,
                    backgroundColor: "rgba(255,255,255,0.08)",
                    borderRadius: 1
                  }}
                />
                <IconButton
                  size="small"
                  onClick={(e) => setUsernameInfoAnchor(e.currentTarget)}
                  sx={{ color: "rgba(144,202,249,0.85)" }}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TextField
                  label="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.08)",
                    borderRadius: 1
                  }}
                />

                <TextField
                  label="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.08)",
                    borderRadius: 1
                  }}
                />
                <IconButton
                  size="small"
                  onClick={(e) => setDisplayNameInfoAnchor(e.currentTarget)}
                  sx={{ color: "rgba(144,202,249,0.85)" }}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Box>

              <BirthdateField value={birthdate} onChange={setBirthdate} />

              {inviteGroupId && (
                <>
                  <TextField
                    label="Password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.08)",
                      borderRadius: 1
                    }}
                  />

                  <FormControl>
                    <FormLabel sx={{ color: "rgba(144,202,249,0.85)" }}>
                      Are you a parent/guardian/caregiver/etc who will be managing a list for someone who cannot manage their own list?
                    </FormLabel>
                    <RadioGroup
                      row
                      value={inviteIsParent}
                      onChange={(e) => setInviteIsParent(e.target.value)}
                    >
                      <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                      <FormControlLabel value="no" control={<Radio />} label="No" />
                    </RadioGroup>
                  </FormControl>
                </>
              )}

              <Button
                variant="contained"
                onClick={handleChooseUsername}
                disabled={loading}
              >
                {loading ? "Saving..." : "Create Profile"}
              </Button>

              {!inviteGroupId && (
                <Button variant="outlined" onClick={resetForm}>
                  Cancel
                </Button>
              )}
            </Stack>
          </>
        ) : mode === "forgotPassword" ? (
          <>
            <Typography variant="h5" sx={{ mb: 1, color: "#90CAF9" }}>
              Reset your password
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: "rgba(144,202,249,0.85)" }}>
              Enter your email address and we'll send you a link to set a new password.
            </Typography>

            {infoMsg && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {infoMsg}
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Stack spacing={2}>
              <TextField
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                sx={{
                  backgroundColor: "rgba(255,255,255,0.08)",
                  borderRadius: 1
                }}
              />

              <Button
                variant="contained"
                onClick={handleForgotPassword}
                disabled={loading}
              >
                {loading ? "Sending..." : "Send Reset Link"}
              </Button>

              <Button
                variant="text"
                onClick={() => {
                  setMode("signIn");
                  setError("");
                  setInfoMsg("");
                }}
                sx={{ color: "#90CAF9" }}
              >
                Back to sign in
              </Button>
            </Stack>
          </>
        ) : mode === "resetPassword" ? (
          <>
            <Typography variant="h5" sx={{ mb: 1, color: "#90CAF9" }}>
              Set a new password
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: "rgba(144,202,249,0.85)" }}>
              Choose a new password for your account.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Stack spacing={2}>
              <TextField
                label="New Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                sx={{
                  backgroundColor: "rgba(255,255,255,0.08)",
                  borderRadius: 1
                }}
              />

              <TextField
                label="Confirm New Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                sx={{
                  backgroundColor: "rgba(255,255,255,0.08)",
                  borderRadius: 1
                }}
              />

              <Button
                variant="contained"
                onClick={handleResetPassword}
                disabled={loading}
              >
                {loading ? "Saving..." : "Set New Password"}
              </Button>
            </Stack>
          </>
        ) : (
          <>
            <Typography variant="h4" sx={{ mb: 3, color: "#90CAF9" }}>
              Welcome to Wishlist Manager
            </Typography>

            {infoMsg && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {infoMsg}
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {mode === "signIn" ? (
              <>
                <TextField
                  label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                  sx={{
                    mb: 2,
                    backgroundColor: "rgba(255,255,255,0.08)",
                    borderRadius: 1
                  }}
                  autoFocus
                />

                <TextField
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  fullWidth
                  sx={{
                    mb: 2,
                    backgroundColor: "rgba(255,255,255,0.08)",
                    borderRadius: 1
                  }}
                />

                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSignIn}
                  disabled={loading}
                >
                  {loading ? "Signing in..." : "Sign In"}
                </Button>

                <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.6)" }}>
                  or
                </Divider>

                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<GoogleLogo />}
                  onClick={() => handleOAuthSignIn("google")}
                  disabled={loading}
                  sx={{
                    mb: 1,
                    backgroundColor: "#FFFFFF",
                    color: "#3C4043",
                    borderColor: "#DADCE0",
                    "&:hover": {
                      backgroundColor: "#F8F8F8",
                      borderColor: "#DADCE0"
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "rgba(255,255,255,0.6)"
                    }
                  }}
                >
                  Continue with Google
                </Button>

                <Button
                  variant="text"
                  fullWidth
                  onClick={() => {
                    setMode("forgotPassword");
                    setError("");
                    setInfoMsg("");
                  }}
                  sx={{ mt: 1, color: "#90CAF9" }}
                >
                  Forgot password?
                </Button>

                <Button
                  variant="text"
                  fullWidth
                  onClick={() => {
                    setMode("signUp");
                    setError("");
                  }}
                  sx={{ color: "#90CAF9" }}
                >
                  Create an account
                </Button>
              </>
            ) : (
              <>
                <TextField
                  label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                  sx={{
                    mb: 2,
                    backgroundColor: "rgba(255,255,255,0.08)",
                    borderRadius: 1
                  }}
                  autoFocus
                />

                <TextField
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  fullWidth
                  sx={{
                    mb: 2,
                    backgroundColor: "rgba(255,255,255,0.08)",
                    borderRadius: 1
                  }}
                />

                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <TextField
                    label="Username"
                    value={desiredUserId}
                    onChange={(e) => setDesiredUserId(e.target.value)}
                    fullWidth
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.08)",
                      borderRadius: 1
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={(e) => setUsernameInfoAnchor(e.currentTarget)}
                    sx={{ color: "rgba(144,202,249,0.85)" }}
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <TextField
                    label="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.08)",
                      borderRadius: 1
                    }}
                  />

                  <TextField
                    label="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.08)",
                      borderRadius: 1
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={(e) => setDisplayNameInfoAnchor(e.currentTarget)}
                    sx={{ color: "rgba(144,202,249,0.85)" }}
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <BirthdateField value={birthdate} onChange={setBirthdate} />
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSignUp}
                  disabled={loading}
                >
                  {loading ? "Creating..." : "Create Account"}
                </Button>

                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1, textAlign: "center" }}>
                  By creating an account, you agree to our{" "}
                  <Link component="button" type="button" variant="caption" onClick={() => setLegalPage("terms")}>Terms of Service</Link>
                  {" "}and{" "}
                  <Link component="button" type="button" variant="caption" onClick={() => setLegalPage("privacy")}>Privacy Policy</Link>.
                </Typography>

                <Button
                  variant="text"
                  fullWidth
                  onClick={() => {
                    setMode("signIn");
                    setError("");
                  }}
                  sx={{ mt: 1, color: "#90CAF9" }}
                >
                  Back to sign in
                </Button>
              </>
            )}
          </>
        )}
      </Paper>
        </Box>
      </div>
      <Popover
        open={Boolean(usernameInfoAnchor)}
        anchorEl={usernameInfoAnchor}
        onClose={() => setUsernameInfoAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        slotProps={{ paper: { sx: { backgroundColor: "#0F2C24", color: "#f8fafc" } } }}
      >
        <Typography variant="body2" sx={{ p: 2, maxWidth: 280 }}>
          <strong>Username</strong> — This is your User ID in Wishlist Manager, only you will see this.
        </Typography>
      </Popover>
      <Popover
        open={Boolean(displayNameInfoAnchor)}
        anchorEl={displayNameInfoAnchor}
        onClose={() => setDisplayNameInfoAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        slotProps={{ paper: { sx: { backgroundColor: "#0F2C24", color: "#f8fafc" } } }}
      >
        <Typography variant="body2" sx={{ p: 2, maxWidth: 280 }}>
          <strong>Display Name</strong> — This will be used as the name of your wishlist that your group members will see. Your list will be auto-created for you using this name but you may edit it later.
        </Typography>
      </Popover>
    </>
  );
}
